import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Modal, Descriptions, message } from 'antd';
import { EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const DeviceManagement = () => {
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  useEffect(() => {
    fetchActivations();
  }, [pagination.current]);

  const fetchActivations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://keyapi.dva.ink/api/admin/activations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: pagination.current,
          limit: pagination.pageSize
        }
      });

      setActivations(response.data.activations);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      message.error('获取设备列表失败');
    } finally {
      setLoading(false);
    }
  };

  const showDeviceDetail = (record) => {
    setSelectedDevice(record);
    setDetailVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '卡密',
      dataIndex: 'card_key',
      key: 'card_key',
      render: (text) => <code>{text}</code>
    },
    {
      title: '设备名称',
      dataIndex: 'device_name',
      key: 'device_name',
    },
    {
      title: '设备ID',
      dataIndex: 'device_id',
      key: 'device_id',
      render: (text) => <code>{text.substring(0, 12)}...</code>
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
    },
    {
      title: '激活时间',
      dataIndex: 'activated_at',
      key: 'activated_at',
      width: 180,
      render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '到期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 180,
      render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '最后验证',
      dataIndex: 'last_verify',
      key: 'last_verify',
      width: 180,
      render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const isExpired = moment().isAfter(record.expires_at);
        const isActive = record.is_active === 1;
        
        if (!isActive) {
          return <Tag color="red">已禁用</Tag>;
        }
        
        if (isExpired) {
          return <Tag color="orange">已过期</Tag>;
        }
        
        // 检查最后验证时间，超过24小时显示离线
        const lastVerify = moment(record.last_verify);
        const hoursAgo = moment().diff(lastVerify, 'hours');
        
        if (hoursAgo > 24) {
          return <Tag color="gray">离线</Tag>;
        }
        
        return <Tag color="green">在线</Tag>;
      }
    },
    {
      title: '剩余天数',
      key: 'remaining_days',
      width: 100,
      render: (_, record) => {
        const remaining = moment(record.expires_at).diff(moment(), 'days');
        return remaining > 0 ? `${remaining} 天` : '已过期';
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <div>
          <Button 
            type="text" 
            icon={<EyeOutlined />}
            onClick={() => showDeviceDetail(record)}
            style={{ marginRight: 8 }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>设备管理</h2>
        <Button onClick={fetchActivations}>
          刷新
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={activations}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          onChange: (page, pageSize) => {
            setPagination(prev => ({ ...prev, current: page, pageSize }));
          }
        }}
        scroll={{ x: 1400 }}
      />

      <Modal
        title="设备详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {selectedDevice && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="设备ID">
              <code>{selectedDevice.device_id}</code>
            </Descriptions.Item>
            <Descriptions.Item label="设备名称">
              {selectedDevice.device_name}
            </Descriptions.Item>
            <Descriptions.Item label="机器码">
              <code>{selectedDevice.machine_code}</code>
            </Descriptions.Item>
            <Descriptions.Item label="IP地址">
              {selectedDevice.ip_address}
            </Descriptions.Item>
            <Descriptions.Item label="使用卡密">
              <code>{selectedDevice.card_key}</code>
            </Descriptions.Item>
            <Descriptions.Item label="有效期">
              {selectedDevice.valid_days} 天
            </Descriptions.Item>
            <Descriptions.Item label="激活时间">
              {moment(selectedDevice.activated_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="到期时间">
              {moment(selectedDevice.expires_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="最后验证">
              {selectedDevice.last_verify 
                ? moment(selectedDevice.last_verify).format('YYYY-MM-DD HH:mm:ss')
                : '从未验证'
              }
            </Descriptions.Item>
            <Descriptions.Item label="剩余天数">
              {moment(selectedDevice.expires_at).diff(moment(), 'days')} 天
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {(() => {
                const isExpired = moment().isAfter(selectedDevice.expires_at);
                const isActive = selectedDevice.is_active === 1;
                
                if (!isActive) {
                  return <Tag color="red">已禁用</Tag>;
                }
                
                if (isExpired) {
                  return <Tag color="orange">已过期</Tag>;
                }
                
                const lastVerify = moment(selectedDevice.last_verify);
                const hoursAgo = moment().diff(lastVerify, 'hours');
                
                if (hoursAgo > 24) {
                  return <Tag color="gray">离线</Tag>;
                }
                
                return <Tag color="green">在线</Tag>;
              })()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default DeviceManagement;
